import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { ObservationAgreementSignatureDetail } from '../../api/types';
import { useHubContext } from '../../shell/HubContext';
import { AgreementText } from '../../observation-agreement/AgreementText';
import { exportAgreementPdf } from '../../observation-agreement/agreementPdf';

/**
 * One signed agreement, on its own page and its own URL.
 *
 * Laid out as the paper form reads — the agreement first, then who signed it and when — because this
 * page answers one question, "what did this person agree to", and the text is the answer. The
 * clauses shown are the ones that were on screen when they ticked the box, snapshotted at signing
 * time, so an agreement revised since still reads as it was signed.
 */
export function SignatureDetailPage() {
  const { api } = useHubContext();
  const { signatureId } = useParams();

  const [signature, setSignature] = useState<ObservationAgreementSignatureDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    if (!signatureId) return;

    const controller = new AbortController();

    (async () => {
      try {
        setSignature(await api.getObservationAgreementSignature(signatureId, controller.signal));
      } catch (err) {
        if (!controller.signal.aborted) {
          setLoadError(err instanceof Error ? err.message : 'Could not load the agreement.');
        }
      }
    })();

    return () => controller.abort();
  }, [api, signatureId]);

  if (loadError) {
    return (
      <div className="page-message page-message--error">
        <h2>Could not load the agreement</h2>
        <p>{loadError}</p>
        <p>
          <Link to="/observation-agreement-review">Back to all signed agreements</Link>
        </p>
      </div>
    );
  }

  if (!signature) {
    return <div className="page-message">Loading…</div>;
  }

  const loaded = signature;
  const signed = new Date(loaded.signedAtUtc);

  async function exportPdf() {
    setExporting(true);
    setExportError(null);

    try {
      // jsPDF arrives here as its own chunk, fetched on this click — see agreementPdf.ts.
      await exportAgreementPdf(loaded);
    } catch (err) {
      console.error('Could not export the agreement as a PDF', err);
      setExportError('The PDF could not be generated. Try again, or reload the page.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="care-page care-page--wide">
      <Link className="care-back" to="/observation-agreement-review">
        ← All signed agreements
      </Link>

      <header className="care-detail__header">
        <div>
          <h1 className="care-page__title">{loaded.signerName}</h1>

          {/* The clinic is optional, so it joins the date with a separator only when there is one —
              otherwise the line opens with a stray middot. */}
          <p className="care-page__subtitle">
            {loaded.location !== null && `${loaded.location} · `}
            {signed.toLocaleDateString()}{' '}
            {signed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </p>

          {loaded.email !== null && (
            <p className="care-page__subtitle">
              <a href={`mailto:${loaded.email}`}>{loaded.email}</a>
            </p>
          )}
        </div>

        <div className="interview-detail__meta">
          <button
            type="button"
            className="button button--quiet"
            disabled={exporting}
            onClick={() => void exportPdf()}
          >
            {exporting ? 'Preparing…' : 'Export PDF'}
          </button>

          <div className="care-table__primary">Agreement v{loaded.agreementVersion}</div>

          {/* Amber, matching the dashboard's tag — the same fact should not change colour depending
              on where you read it. */}
          {!loaded.isCurrentVersion && <div className="care-tag">No longer the current version</div>}
        </div>
      </header>

      {exportError && (
        <div className="issues__block issues__block--error">
          <p>{exportError}</p>
        </div>
      )}

      <section className="care-review__group agreement-record">
        <h2 className="interview__section-title">{loaded.agreementTitle}</h2>

        <div className="agreement-record__document">
          <AgreementText clauses={loaded.clauses} />
          <p className="agreement__attribution">{loaded.attribution}</p>
        </div>

        {/* The acceptance itself, set apart from the text it was given for: what they ticked, the
            name they signed with, and the moment the API recorded it. Together these are the whole
            record — there is nothing else stored about this signature. */}
        <div className="agreement-record__signature">
          <p className="agreement-record__consent">“{loaded.consentStatement}”</p>

          <dl className="agreement-record__fields">
            <div>
              <dt>Signature</dt>
              <dd className="agreement-record__signed-name">{loaded.signerName}</dd>
            </div>
            {/* Listed even when empty. This block is the record of what was collected, and a field
                silently missing from it would read as one that was never asked for. */}
            <div>
              <dt>Email</dt>
              <dd>
                {loaded.email === null ? (
                  <span className="agreement-record__absent">Not given</span>
                ) : (
                  <a href={`mailto:${loaded.email}`}>{loaded.email}</a>
                )}
              </dd>
            </div>
            <div>
              <dt>Clinic</dt>
              <dd>
                {loaded.location ?? <span className="agreement-record__absent">Not given</span>}
              </dd>
            </div>
            <div>
              <dt>Date</dt>
              <dd>
                {signed.toLocaleDateString()}{' '}
                {signed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </dd>
            </div>
          </dl>
        </div>
      </section>
    </div>
  );
}
